import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Building2, BadgeCheck, Clock, Users, DollarSign, ShoppingBag, Gift, TrendingUp, Loader2, ArrowRight,
} from "lucide-react";
import api from "@/lib/api";

function Stat({ icon: Icon, label, value, hint, accent = "text-primary" }) {
  return (
    <div className="bg-[#1A1814] border border-white/10 rounded-xl p-6">
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">{label}</p>
        <Icon className={`h-5 w-5 ${accent}`} strokeWidth={1.5} />
      </div>
      <p className="mt-3 font-heading text-3xl font-semibold text-white">{value}</p>
      {hint && <p className="text-sm text-stone-500 mt-1">{hint}</p>}
    </div>
  );
}

const planLabel = { professional: "Professional", starter: "Starter", trial: "Trial" };

export default function AdminOverview() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/overview").then((r) => setD(r.data)); }, []);

  if (!d) return <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>;
  const money = (n) => `$${Number(n || 0).toLocaleString()}`;

  return (
    <div data-testid="admin-overview-page">
      <div className="mb-8">
        <h1 className="font-heading text-3xl sm:text-4xl font-medium text-white">Platform Overview</h1>
        <p className="text-stone-400 mt-1">Everything across all your client restaurants, at a glance.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Building2} label="Total Clients" value={d.total_clients} hint="Restaurant accounts" />
        <Stat icon={BadgeCheck} label="Active Subscriptions" value={d.active_subscriptions} accent="text-emerald-400" />
        <Stat icon={Clock} label="On Free Trial" value={d.trials} accent="text-amber-400" />
        <Stat icon={TrendingUp} label="Est. MRR" value={money(d.mrr)} hint="Active plans" accent="text-emerald-400" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
        <Stat icon={DollarSign} label="Client GMV" value={money(d.platform_sales)} hint="Total recorded sales" />
        <Stat icon={ShoppingBag} label="Total Orders" value={d.total_orders} accent="text-stone-300" />
        <Stat icon={Users} label="Staff Accounts" value={d.total_staff} accent="text-stone-300" />
        <Stat icon={Gift} label="Gift Card Liability" value={money(d.outstanding_gift)} hint="Outstanding balance" accent="text-primary" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-2 bg-[#1A1814] border border-white/10 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-white">Recent signups</h3>
            <Link to="/admin/clients" className="text-sm text-primary hover:underline flex items-center gap-1">
              All clients <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          <div className="divide-y divide-white/5">
            {d.recent_clients.map((c) => (
              <Link key={c.id} to={`/admin/clients/${c.id}`} data-testid={`recent-client-${c.id}`}
                className="flex items-center justify-between py-3 hover:bg-white/5 -mx-2 px-2 rounded-lg transition-colors">
                <div className="flex items-center gap-3">
                  <span className="h-9 w-9 rounded-lg bg-white/5 flex items-center justify-center font-heading font-semibold text-white">{c.restaurant?.charAt(0) || "?"}</span>
                  <div>
                    <p className="text-sm font-medium text-white">{c.restaurant}</p>
                    <p className="text-xs text-stone-500">{c.name} · {c.email}</p>
                  </div>
                </div>
                <span className={`text-xs px-2.5 py-1 rounded-full ${c.status === "active" ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400"}`}>
                  {planLabel[c.plan] || c.plan}
                </span>
              </Link>
            ))}
          </div>
        </div>

        <div className="bg-[#1A1814] border border-white/10 rounded-xl p-6">
          <h3 className="text-lg font-medium text-white mb-4">Plan distribution</h3>
          <div className="space-y-4">
            {["professional", "starter", "trial"].map((p) => {
              const cnt = d.plan_counts[p] || 0;
              const pct = d.total_clients ? Math.round((cnt / d.total_clients) * 100) : 0;
              return (
                <div key={p}>
                  <div className="flex justify-between text-sm mb-1.5">
                    <span className="text-stone-300">{planLabel[p]}</span>
                    <span className="text-stone-500">{cnt} · {pct}%</span>
                  </div>
                  <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                    <div className={`h-full ${p === "professional" ? "bg-emerald-400" : p === "starter" ? "bg-primary" : "bg-amber-400"}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
